// src/plugin.ts
import {
  createClient as createVanillaClient
} from "@wagmi/core";
import {
  inject,
  markRaw,
  readonly,
  shallowRef,
  triggerRef
} from "vue";
import { QueryClient, VueQueryPlugin } from "vue-query";
function createClient({
  queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        cacheTime: 1e3 * 60 * 60 * 24,
        refetchOnWindowFocus: false,
        retry: 0
      },
      mutations: {}
    }
  }),
  ...config
}) {
  const client = createVanillaClient(config);
  return Object.assign(client, { queryClient });
}
var VagmiClientKey = Symbol("vagmi");
function VagmiPlugin(client) {
  return {
    install(app) {
      app.use(VueQueryPlugin, {
        queryClient: client.queryClient
      });
      const reactiveClient = shallowRef(client);
      if (client.config.autoConnect)
        client.autoConnect();
      const unsubscribe = client.subscribe(() => {
        triggerRef(markRaw(reactiveClient));
      });
      const originalUnmount = app.unmount;
      app.unmount = function vagmiUnmount() {
        unsubscribe();
        originalUnmount();
      };
      app.provide(VagmiClientKey, markRaw(reactiveClient));
    }
  };
}
function useClient() {
  const vagmiClient = inject(VagmiClientKey);
  if (!vagmiClient) {
    throw new Error([
      "`useClient` must be used within `WagmiConfig`.\n",
      "Read more: https://wagmi.sh/docs/WagmiConfig"
    ].join("\n"));
  }
  return vagmiClient;
}
function useReadonlyClient() {
  const client = useClient();
  return readonly(client);
}

// src/composables/accounts/useAccount.ts
import { getAccount, watchAccount } from "@wagmi/core";
import { toRefs, tryOnScopeDispose } from "@vueuse/core";
import { ref, watchEffect } from "vue";
function useAccount({
  onConnect,
  onDisconnect
} = {}) {
  const client = useClient();
  const initialState = getAccount();
  const account = ref(initialState);
  const unwatch = watchAccount((data) => {
    account.value = data;
  });
  tryOnScopeDispose(() => {
    unwatch();
  });
  watchEffect((onInvalidate) => {
    if (!onConnect && !onDisconnect)
      return;
    const unsubscribe = client.value.subscribe((state) => state.status, (status, prevStatus) => {
      if (!!onConnect && status === "connected") {
        const { address, connector } = getAccount();
        onConnect({
          address,
          connector,
          isReconnected: prevStatus === "reconnecting"
        });
      }
      if (!!onDisconnect && prevStatus !== "connecting" && status === "disconnected")
        onDisconnect();
    });
    onInvalidate(unsubscribe);
  });
  return toRefs(account);
}

// src/composables/accounts/useConnect.ts
import { Connector, connect } from "@wagmi/core";
import { computed, reactive } from "vue";
import { useMutation } from "vue-query";
import { toRefs as toRefs2 } from "@vueuse/core";

// src/utils/get-maybe-ref-value.ts
import { isRef } from "vue";
function getMaybeRefValue(item) {
  return isRef(item) ? item.value : item;
}

// src/composables/accounts/useConnect.ts
var mutationKey = (args) => [
  { entity: "connect", ...args }
];
var mutationFn = (args) => {
  const { connector, chainId: chainId2 } = args;
  if (!connector)
    throw new Error("connector is required");
  return connect({ connector, chainId: chainId2 });
};
function useConnect({
  chainId: chainId2,
  connector,
  onBeforeConnect,
  onConnect,
  onError,
  onSettled
} = {}) {
  const client = useClient();
  const options = reactive({
    mutationKey: computed(() => mutationKey({ connector: getMaybeRefValue(connector), chainId: getMaybeRefValue(chainId2) })),
    mutationFn,
    onError,
    onMutate: onBeforeConnect,
    onSettled,
    onSuccess: onConnect
  });
  const { data, error, mutate, mutateAsync, reset, status, variables } = useMutation(options);
  const connect2 = (connectorOrArgs) => {
    let config;
    if (connectorOrArgs instanceof Connector) {
      const connector_ = connectorOrArgs;
      config = {
        chainId: getMaybeRefValue(chainId2),
        connector: connector_ ?? getMaybeRefValue(connector)
      };
    } else {
      const args = connectorOrArgs;
      config = {
        chainId: (args == null ? void 0 : args.chainId) ?? getMaybeRefValue(chainId2),
        connector: (args == null ? void 0 : args.connector) ?? getMaybeRefValue(connector)
      };
    }
    return mutate(config);
  };
  const connectAsync = (connectorOrArgs) => {
    let config;
    if (connectorOrArgs instanceof Connector) {
      const connector_ = connectorOrArgs;
      config = {
        chainId: getMaybeRefValue(chainId2),
        connector: connector_ ?? getMaybeRefValue(connector)
      };
    } else {
      const args = connectorOrArgs;
      config = {
        chainId: (args == null ? void 0 : args.chainId) ?? getMaybeRefValue(chainId2),
        connector: (args == null ? void 0 : args.connector) ?? getMaybeRefValue(connector)
      };
    }
    return mutateAsync(config);
  };
  const status_ = computed(() => {
    let result2;
    if (client.value.status === "reconnecting")
      result2 = "reconnecting";
    else if (status.value === "loading" || client.value.status === "connecting")
      result2 = "connecting";
    else if (client.value.connector)
      result2 = "connected";
    else if (!client.value.connector || status.value === "success")
      result2 = "disconnected";
    else
      result2 = status.value;
    return result2;
  });
  const result = computed(() => {
    var _a;
    return {
      activeConnector: client.value.connector,
      connect: connect2,
      connectAsync,
      connectors: client.value.connectors,
      data: data.value,
      error: error.value,
      isConnected: status_.value === "connected",
      isConnecting: status_.value === "connecting",
      isDisconnected: status_.value === "disconnected",
      isError: status.value === "error",
      isIdle: status_.value === "idle",
      isReconnecting: status_.value === "reconnecting",
      pendingConnector: (_a = variables.value) == null ? void 0 : _a.connector,
      reset,
      status: status_.value
    };
  });
  return toRefs2(result);
}

// src/composables/accounts/useDisconnect.ts
import { disconnect } from "@wagmi/core";
import { useMutation as useMutation2 } from "vue-query";
var mutationKey2 = [{ entity: "disconnect" }];
var mutationFn2 = () => disconnect();
function useDisconnect({
  onError,
  onMutate,
  onSettled,
  onSuccess
} = {}) {
  const {
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    mutate: disconnect2,
    mutateAsync: disconnectAsync,
    reset,
    status
  } = useMutation2(mutationKey2, mutationFn2, {
    ...onError ? {
      onError(error2, _variables, context) {
        onError(error2, context);
      }
    } : {},
    onMutate,
    ...onSettled ? {
      onSettled(_data, error2, _variables, context) {
        onSettled(error2, context);
      }
    } : {},
    onSuccess(_data, _variables, context) {
      onSuccess == null ? void 0 : onSuccess(context);
    }
  });
  return {
    disconnect: disconnect2,
    disconnectAsync,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    reset,
    status
  };
}

// src/composables/accounts/useNetwork.ts
import { toRefs as toRefs3, tryOnScopeDispose as tryOnScopeDispose2 } from "@vueuse/core";
import {
  getNetwork,
  watchNetwork
} from "@wagmi/core";
import { ref as ref2 } from "vue";
function useNetwork() {
  const network = ref2(getNetwork());
  const unwatch = watchNetwork((data) => {
    network.value = data;
  });
  tryOnScopeDispose2(() => {
    unwatch();
  });
  return toRefs3(network);
}

// src/composables/accounts/useSwitchNetwork.ts
import {
  switchNetwork
} from "@wagmi/core";
import { computed as computed2, reactive as reactive2 } from "vue";
import { useMutation as useMutation3 } from "vue-query";
var mutationKey3 = (args) => [
  { entity: "switchNetwork", ...args }
];
var mutationFn3 = (args) => {
  const { chainId: chainId2 } = args;
  if (!chainId2)
    throw new Error("chainId is required");
  return switchNetwork({ chainId: chainId2 });
};
function useSwitchNetwork({
  chainId: chainId2,
  onError,
  onMutate,
  onSettled,
  onSuccess
} = {}) {
  const client = useClient();
  const options = reactive2({
    mutationKey: computed2(() => mutationKey3({ chainId: getMaybeRefValue(chainId2) })),
    mutationFn: mutationFn3,
    onError,
    onMutate,
    onSettled,
    onSuccess
  });
  const {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    mutate,
    mutateAsync,
    reset,
    status,
    variables
  } = useMutation3(options);
  const switchNetwork_ = (chainId_) => mutate({ chainId: chainId_ ?? getMaybeRefValue(chainId2) });
  const switchNetworkAsync_ = (chainId_) => mutateAsync({ chainId: chainId_ ?? getMaybeRefValue(chainId2) });
  const connector = computed2(() => client.value.connector);
  return {
    chains: computed2(() => client.value.chains ?? []),
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    pendingChainId: computed2(() => {
      var _a;
      return (_a = variables.value) == null ? void 0 : _a.chainId;
    }),
    reset,
    status,
    switchNetwork: computed2(() => {
      var _a;
      return ((_a = connector.value) == null ? void 0 : _a.switchChain) ? switchNetwork_ : void 0;
    }),
    switchNetworkAsync: computed2(() => {
      var _a;
      return ((_a = connector.value) == null ? void 0 : _a.switchChain) ? switchNetworkAsync_ : void 0;
    }),
    variables
  };
}

// src/composables/accounts/useBalance.ts
import { fetchBalance } from "@wagmi/core";
import { computed as computed6, reactive as reactive5, watch } from "vue";

// src/composables/utils/useChainId.ts
import { computed as computed3 } from "vue";

// src/composables/providers/useProvider.ts
import { getProvider, watchProvider } from "@wagmi/core";
import { markRaw as markRaw2, readonly as readonly2, ref as ref3, watchEffect as watchEffect2 } from "vue";
function useProvider({
  chainId: chainId2
} = {}) {
  const provider = ref3(markRaw2(getProvider({ chainId: getMaybeRefValue(chainId2) })));
  watchEffect2((onInvalidate) => {
    const unwatch = watchProvider({ chainId: getMaybeRefValue(chainId2) }, (provider_) => {
      provider.value = markRaw2(provider_);
    });
    onInvalidate(() => {
      unwatch();
    });
  });
  return readonly2(provider);
}

// src/composables/providers/useWebSocketProvider.ts
import {
  getWebSocketProvider,
  watchWebSocketProvider
} from "@wagmi/core";
import { ref as ref4, watchEffect as watchEffect3 } from "vue";
function useWebSocketProvider({ chainId: chainId2 } = {}) {
  const initialValue = getMaybeRefValue(chainId2);
  const webSocketProvider = ref4(getWebSocketProvider({ chainId: initialValue }));
  watchEffect3((onInvalidate) => {
    const unwatch = watchWebSocketProvider({ chainId: getMaybeRefValue(chainId2) }, (webSocketProvider_) => {
      webSocketProvider.value = webSocketProvider_;
    });
    onInvalidate(() => {
      unwatch();
    });
  });
  return webSocketProvider;
}

// src/composables/utils/useChainId.ts
function useChainId({ chainId: chainId2 } = {}) {
  const provider = useProvider({ chainId: chainId2 });
  return computed3(() => {
    var _a, _b;
    return (_b = (_a = provider.value) == null ? void 0 : _a.network) == null ? void 0 : _b.chainId;
  });
}

// src/composables/utils/useQuery.ts
import { useQuery as _useQuery } from "vue-query";
function useQuery(...args) {
  const result = _useQuery(...args);
  const {
    dataUpdatedAt,
    errorUpdatedAt,
    failureCount,
    isFetchedAfterMount,
    isLoadingError,
    isPaused,
    isPlaceholderData,
    isPreviousData,
    isRefetchError,
    isStale,
    remove,
    ...rest
  } = result;
  const internal = {
    dataUpdatedAt,
    errorUpdatedAt,
    failureCount,
    isFetchedAfterMount,
    isLoadingError,
    isPaused,
    isPlaceholderData,
    isPreviousData,
    isRefetchError,
    isStale,
    remove
  };
  return {
    ...rest,
    internal
  };
}

// src/composables/network-status/useBlockNumber.ts
import { useQueryClient } from "vue-query";
import {
  fetchBlockNumber
} from "@wagmi/core";
import { computed as computed4, reactive as reactive3, watchEffect as watchEffect4 } from "vue";
var queryKey = ({ chainId: chainId2 }) => [{ entity: "blockNumber", chainId: chainId2 }];
var queryFn = ({
  queryKey: [{ chainId: chainId2 }]
}) => {
  return fetchBlockNumber({ chainId: chainId2 });
};
function useBlockNumber({
  cacheTime = 0,
  chainId: chainId_,
  enabled = true,
  staleTime,
  suspense,
  watch: watch2 = false,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId2 = useChainId({ chainId: chainId_ });
  const provider = useProvider();
  const webSocketProvider = useWebSocketProvider();
  const queryClient = useQueryClient();
  const listener = (blockNumber) => {
    queryClient.setQueryData(queryKey({ chainId: getMaybeRefValue(chainId2) }), blockNumber);
  };
  const provider_ = computed4(() => webSocketProvider.value ?? provider.value);
  watchEffect4((onInvalidate) => {
    if (!getMaybeRefValue(watch2))
      return;
    provider_.value.on("block", listener);
    onInvalidate(() => {
      provider_.value.off("block", listener);
    });
  });
  const options = reactive3({
    queryKey: computed4(() => queryKey({ chainId: getMaybeRefValue(chainId2) })),
    queryFn,
    cacheTime,
    enabled,
    staleTime,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
  return useQuery(options);
}

// src/composables/network-status/useFeeData.ts
import { fetchFeeData } from "@wagmi/core";
import { computed as computed5, reactive as reactive4, watch as vWatch } from "vue";
var queryKey2 = ({
  chainId: chainId2,
  formatUnits
}) => [{ entity: "feeData", chainId: chainId2, formatUnits }];
var queryFn2 = ({
  queryKey: [{ chainId: chainId2, formatUnits }]
}) => {
  return fetchFeeData({ chainId: chainId2, formatUnits });
};
function useFeeData({
  cacheTime,
  chainId: chainId_,
  enabled = true,
  formatUnits = "wei",
  staleTime,
  suspense,
  watch: watch2,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId2 = useChainId({ chainId: chainId_ });
  const options = reactive4({
    queryKey: computed5(() => queryKey2({ chainId: getMaybeRefValue(chainId2), formatUnits: getMaybeRefValue(formatUnits) })),
    queryFn: queryFn2,
    cacheTime,
    enabled,
    staleTime,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
  const feeDataQuery = useQuery(options);
  const { data: blockNumber } = useBlockNumber({ watch: watch2 });
  vWatch(blockNumber, () => {
    if (!getMaybeRefValue(enabled))
      return;
    if (!getMaybeRefValue(watch2))
      return;
    if (!getMaybeRefValue(blockNumber))
      return;
    feeDataQuery.refetch();
  }, {
    immediate: true
  });
  return feeDataQuery;
}

// src/composables/accounts/useBalance.ts
var queryKey3 = ({
  addressOrName,
  chainId: chainId2,
  formatUnits,
  token
}) => [{ entity: "balance", addressOrName, chainId: chainId2, formatUnits, token }];
var queryFn3 = ({
  queryKey: [{ addressOrName, chainId: chainId2, formatUnits, token }]
}) => {
  if (!addressOrName)
    throw new Error("address is required");
  return fetchBalance({ addressOrName, chainId: chainId2, formatUnits, token });
};
function useBalance({
  addressOrName,
  cacheTime,
  chainId: chainId_,
  enabled = true,
  formatUnits = "ether",
  staleTime,
  suspense,
  token,
  watch: watch_,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId2 = useChainId({ chainId: chainId_ });
  const options = reactive5({
    queryKey: computed6(() => queryKey3({
      addressOrName: getMaybeRefValue(addressOrName),
      chainId: getMaybeRefValue(chainId2),
      formatUnits: getMaybeRefValue(formatUnits),
      token: getMaybeRefValue(token)
    })),
    queryFn: queryFn3,
    cacheTime,
    enabled: computed6(() => Boolean(getMaybeRefValue(enabled) && getMaybeRefValue(addressOrName))),
    staleTime,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
  const balanceQuery = useQuery(options);
  const { data: blockNumber } = useBlockNumber({ watch: watch_ });
  watch(blockNumber, () => {
    if (!getMaybeRefValue(enabled))
      return;
    if (!getMaybeRefValue(watch))
      return;
    if (!getMaybeRefValue(blockNumber))
      return;
    if (!getMaybeRefValue(addressOrName))
      return;
    balanceQuery.refetch();
  }, {
    immediate: true
  });
  return balanceQuery;
}

// src/composables/accounts/useSigner.ts
import { fetchSigner, watchSigner } from "@wagmi/core";
import { useQueryClient as useQueryClient2 } from "vue-query";
import { tryOnScopeDispose as tryOnScopeDispose3 } from "@vueuse/core";
var queryKey4 = () => [{ entity: "signer" }];
var queryFn4 = () => fetchSigner();
function useSigner({
  onError,
  onSettled,
  onSuccess
} = {}) {
  const signerQuery = useQuery(queryKey4(), queryFn4, {
    cacheTime: 0,
    staleTime: 0,
    onError,
    onSettled,
    onSuccess
  });
  const queryClient = useQueryClient2();
  const unwatch = watchSigner((signer) => queryClient.setQueryData(queryKey4(), signer));
  tryOnScopeDispose3(() => {
    unwatch();
  });
  return signerQuery;
}

// src/composables/accounts/useSignMessage.ts
import { signMessage } from "@wagmi/core";
import { computed as computed7, reactive as reactive6 } from "vue";
import { useMutation as useMutation4 } from "vue-query";
var mutationKey4 = (args) => [
  { entity: "signMessage", ...args }
];
var mutationFn4 = (args) => {
  const { message } = args;
  if (!message)
    throw new Error("message is required");
  return signMessage({ message });
};
function useSignMessage({
  message,
  onError,
  onMutate,
  onSettled,
  onSuccess
} = {}) {
  const options = reactive6({
    mutationKey: computed7(() => mutationKey4({ message: getMaybeRefValue(message) })),
    mutationFn: mutationFn4,
    onError,
    onMutate,
    onSettled,
    onSuccess
  });
  const {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    mutate,
    mutateAsync,
    reset,
    status,
    variables
  } = useMutation4(options);
  const signMessage2 = (args) => mutate(args || { message });
  const signMessageAsync = (args) => mutateAsync(args || { message });
  return {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    reset,
    signMessage: signMessage2,
    signMessageAsync,
    status,
    variables
  };
}

// src/composables/accounts/useSignTypedData.ts
import {
  signTypedData
} from "@wagmi/core";
import { computed as computed8, reactive as reactive7 } from "vue";
import { useMutation as useMutation5 } from "vue-query";
var mutationKey5 = (args) => [
  { entity: "signTypedData", ...args }
];
var mutationFn5 = (args) => {
  const { domain, types, value } = args;
  if (!domain || !types || !value)
    throw new Error("domain, types, and value are all required");
  return signTypedData({ domain, types, value });
};
function useSignTypedData({
  domain,
  types,
  value,
  onError,
  onMutate,
  onSettled,
  onSuccess
} = {}) {
  const options = reactive7({
    mutationKey: computed8(() => mutationKey5({
      domain: getMaybeRefValue(domain),
      types: getMaybeRefValue(types),
      value: getMaybeRefValue(value)
    })),
    mutationFn: mutationFn5,
    onError,
    onMutate,
    onSettled,
    onSuccess
  });
  const {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    mutate,
    mutateAsync,
    reset,
    status,
    variables
  } = useMutation5(options);
  const signTypedData2 = (args) => mutate(args || { domain, types, value });
  const signTypedDataAsync = (args) => mutateAsync(args || { domain, types, value });
  return {
    data,
    error,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    reset,
    signTypedData: signTypedData2,
    signTypedDataAsync,
    status,
    variables
  };
}

// src/composables/ens/useEnsName.ts
import { fetchEnsName } from "@wagmi/core";
import { computed as computed9, reactive as reactive8 } from "vue";
var queryKey5 = ({
  address,
  chainId: chainId2
}) => [{ entity: "ensName", address, chainId: chainId2 }];
var queryFn5 = ({
  queryKey: [{ address }]
}) => {
  if (!address)
    throw new Error("address is required");
  return fetchEnsName({ address });
};
function useEnsName({
  address,
  cacheTime,
  chainId: chainId_,
  enabled = true,
  staleTime = 1e3 * 60 * 60 * 24,
  suspense,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId2 = useChainId({ chainId: chainId_ });
  const options = reactive8({
    queryKey: computed9(() => queryKey5({ address: getMaybeRefValue(address), chainId: getMaybeRefValue(chainId2) })),
    queryFn: queryFn5,
    cacheTime,
    enabled: computed9(() => {
      return Boolean(getMaybeRefValue(enabled) && getMaybeRefValue(address) && getMaybeRefValue(chainId2));
    }),
    staleTime,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
  return useQuery(options);
}

// src/composables/ens/useEnsAddress.ts
import {
  fetchEnsAddress
} from "@wagmi/core";
import { computed as computed10, reactive as reactive9 } from "vue";
var queryKey6 = ({
  chainId: chainId2,
  name
}) => [{ entity: "ensAddress", chainId: chainId2, name }];
var queryFn6 = ({
  queryKey: [{ chainId: chainId2, name }]
}) => {
  if (!name)
    throw new Error("name is required");
  return fetchEnsAddress({ chainId: chainId2, name });
};
function useEnsAddress({
  cacheTime,
  chainId: chainId_,
  enabled = true,
  name,
  staleTime = 1e3 * 60 * 60 * 24,
  suspense,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId2 = useChainId({ chainId: chainId_ });
  const options = reactive9({
    queryKey: computed10(() => queryKey6({ chainId: getMaybeRefValue(chainId2), name: getMaybeRefValue(name) })),
    queryFn: queryFn6,
    cacheTime,
    enabled: computed10(() => {
      return Boolean(getMaybeRefValue(enabled) && getMaybeRefValue(name) && getMaybeRefValue(chainId2));
    }),
    staleTime,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
  return useQuery(options);
}

// src/composables/ens/useEnsResolver.ts
import {
  fetchEnsResolver
} from "@wagmi/core";
import { computed as computed11, reactive as reactive10 } from "vue";
var queryKey7 = ({
  chainId: chainId2,
  name
}) => [{ entity: "ensResolver", chainId: chainId2, name }];
var queryFn7 = ({
  queryKey: [{ chainId: chainId2, name }]
}) => {
  if (!name)
    throw new Error("name is required");
  return fetchEnsResolver({ chainId: chainId2, name });
};
function useEnsResolver({
  cacheTime,
  chainId: chainId_,
  enabled = true,
  name,
  staleTime = 1e3 * 60 * 60 * 24,
  suspense,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId2 = useChainId({ chainId: chainId_ });
  const options = reactive10({
    queryKey: computed11(() => queryKey7({ chainId: getMaybeRefValue(chainId2), name: getMaybeRefValue(name) })),
    queryFn: queryFn7,
    cacheTime,
    enabled: computed11(() => {
      return Boolean(getMaybeRefValue(enabled) && getMaybeRefValue(name) && getMaybeRefValue(chainId2));
    }),
    staleTime,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
  return useQuery(options);
}

// src/composables/ens/useEnsAvatar.ts
import {
  fetchEnsAvatar
} from "@wagmi/core";
import { computed as computed12, reactive as reactive11 } from "vue";
var queryKey8 = ({
  addressOrName,
  chainId: chainId2
}) => [{ entity: "ensAvatar", addressOrName, chainId: chainId2 }];
var queryFn8 = ({
  queryKey: [{ addressOrName, chainId: chainId2 }]
}) => {
  if (!addressOrName)
    throw new Error("addressOrName is required");
  return fetchEnsAvatar({ addressOrName, chainId: chainId2 });
};
function useEnsAvatar({
  addressOrName,
  cacheTime,
  chainId: chainId_,
  enabled = true,
  staleTime = 1e3 * 60 * 60 * 24,
  suspense,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId2 = useChainId({ chainId: chainId_ });
  const options = reactive11({
    queryKey: computed12(() => queryKey8({ chainId: getMaybeRefValue(chainId2), addressOrName: getMaybeRefValue(addressOrName) })),
    queryFn: queryFn8,
    cacheTime,
    enabled: computed12(() => {
      return Boolean(getMaybeRefValue(enabled) && getMaybeRefValue(addressOrName) && getMaybeRefValue(chainId2));
    }),
    staleTime,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
  return useQuery(options);
}

// src/composables/transactions/useWaitForTransaction.ts
import { get } from "@vueuse/core";
import {
  waitForTransaction
} from "@wagmi/core";
var queryKey9 = ({
  confirmations,
  chainId: chainId2,
  hash,
  timeout,
  wait
}) => [
  {
    entity: "waitForTransaction",
    confirmations,
    chainId: chainId2,
    hash,
    timeout,
    wait
  }
];
var queryFn9 = ({
  queryKey: [{ chainId: chainId2, confirmations, hash, timeout, wait }]
}) => {
  return waitForTransaction({ chainId: chainId2, confirmations, hash, timeout, wait });
};
function useWaitForTransaction({
  chainId: chainId_,
  confirmations,
  hash,
  timeout,
  wait,
  cacheTime,
  enabled = true,
  staleTime,
  suspense,
  onError,
  onSettled,
  onSuccess
} = {}) {
  const chainId2 = useChainId({ chainId: chainId_ });
  return useQuery(queryKey9({ chainId: get(chainId2), confirmations, hash, timeout, wait }), queryFn9, {
    cacheTime,
    enabled: Boolean(enabled && (hash || wait)),
    staleTime,
    suspense,
    onError,
    onSettled,
    onSuccess
  });
}

// src/index.ts
import {
  AddChainError,
  ChainDoesNotSupportMulticallError,
  ChainMismatchError,
  ChainNotConfiguredError,
  Client,
  Connector as Connector2,
  ConnectorAlreadyConnectedError,
  ConnectorNotFoundError,
  ProviderChainsNotFound,
  ProviderRpcError,
  ResourceUnavailableError,
  RpcError,
  SwitchChainError,
  SwitchChainNotSupportedError,
  UserRejectedRequestError,
  alchemyRpcUrls,
  allChains,
  chain,
  chainId,
  configureChains,
  createStorage,
  defaultChains,
  defaultL2Chains,
  erc20ABI,
  erc721ABI,
  etherscanBlockExplorers,
  infuraRpcUrls,
  publicRpcUrls,
  readContracts
} from "@wagmi/core";
export {
  AddChainError,
  ChainDoesNotSupportMulticallError,
  ChainMismatchError,
  ChainNotConfiguredError,
  Client,
  Connector2 as Connector,
  ConnectorAlreadyConnectedError,
  ConnectorNotFoundError,
  VagmiPlugin as Plugin,
  ProviderChainsNotFound,
  ProviderRpcError,
  ResourceUnavailableError,
  RpcError,
  SwitchChainError,
  SwitchChainNotSupportedError,
  UserRejectedRequestError,
  VagmiPlugin,
  alchemyRpcUrls,
  allChains,
  chain,
  chainId,
  configureChains,
  createClient,
  createStorage,
  defaultChains,
  defaultL2Chains,
  erc20ABI,
  erc721ABI,
  etherscanBlockExplorers,
  infuraRpcUrls,
  publicRpcUrls,
  readContracts,
  useAccount,
  useBalance,
  useBlockNumber,
  useReadonlyClient as useClient,
  useConnect,
  useDisconnect,
  useEnsAddress,
  useEnsAvatar,
  useEnsName,
  useEnsResolver,
  useFeeData,
  useNetwork,
  useProvider,
  useSignMessage,
  useSignTypedData,
  useSigner,
  useSwitchNetwork,
  useWaitForTransaction,
  useWebSocketProvider
};
